/**
 * Pure helpers for the 3D terrain mesh (docs/DEM_TERRAIN.md, section 6). No three.js,
 * no DOM: this module is bundled into the geo worker (task `terrainMesh`).
 *
 * Local frame (shared with SubsurfaceViewer3D's fences): x = east metres, z = north
 * metres from `origin` {lon0, lat0}, y = elevation - refElev (vertical exaggeration is a
 * y-scale on the group that holds the terrain, so it never needs a rebuild).
 * Grids are row-major with row 0 = south, column 0 = west: index k = row * n + col.
 */

export const M_PER_DEG_LAT = 110540;
export const mPerDegLon = (lat0) => 111320 * Math.cos((lat0 * Math.PI) / 180);
export const MAX_GRID = 256;
export const DEFAULT_GRID = 128;

export function toLocal(lon, lat, origin) {
  return [(lon - origin.lon0) * mPerDegLon(origin.lat0), (lat - origin.lat0) * M_PER_DEG_LAT];
}

/** WGS84 bbox {west, south, east, north} of fence lines and voxel cells, padded by `pad`
 *  of its size and at least `minSize` metres across. Null when nothing is placed. */
export function sceneExtent(models = [], { pad = 0.25, minSize = 200 } = {}) {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  const add = (lon, lat) => {
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    if (lon < w) w = lon; if (lon > e) e = lon; if (lat < s) s = lat; if (lat > n) n = lat;
  };
  models.forEach((m) => {
    (m?.line || []).forEach((p) => add(p[0], p[1]));
    const cells = m?.cells || [];
    const step = Math.max(1, Math.floor(cells.length / 5000));
    for (let i = 0; i < cells.length; i += step) if (cells[i].lon !== undefined) add(cells[i].lon, cells[i].lat);
  });
  if (!Number.isFinite(w)) return null;
  const lat0 = (s + n) / 2;
  const mLon = mPerDegLon(lat0);
  const halfW = Math.max(((e - w) * mLon) * (1 + 2 * pad), minSize) / 2;
  const halfH = Math.max(((n - s) * M_PER_DEG_LAT) * (1 + 2 * pad), minSize) / 2;
  const half = Math.max(halfW, halfH); // square, so the N x N grid has square cells
  const cx = (w + e) / 2, cy = lat0;
  return { west: cx - half / mLon, east: cx + half / mLon, south: cy - half / M_PER_DEG_LAT, north: cy + half / M_PER_DEG_LAT };
}

/** Width/height of an extent in metres. */
export function extentSize(extent) {
  const lat0 = (extent.south + extent.north) / 2;
  return [(extent.east - extent.west) * mPerDegLon(lat0), (extent.north - extent.south) * M_PER_DEG_LAT];
}

/** Grid size: `n` (capped at MAX_GRID) and its cell spacing in metres. */
export function gridSpec(extent, n = DEFAULT_GRID) {
  const size = Math.max(2, Math.min(MAX_GRID, Math.round(n)));
  const [wm, hm] = extentSize(extent);
  return { n: size, spacing: Math.max(wm, hm) / (size - 1) };
}

/** [[lon, lat]...] of an n x n grid over the extent (row 0 = south). */
export function gridLonLats(extent, n) {
  const pts = new Array(n * n);
  for (let r = 0; r < n; r++) {
    const lat = extent.south + ((extent.north - extent.south) * r) / (n - 1);
    for (let c = 0; c < n; c++) pts[r * n + c] = [extent.west + ((extent.east - extent.west) * c) / (n - 1), lat];
  }
  return pts;
}

/** Fill nulls/NaN by repeated averaging of valid 4-neighbours (edges of the DEM, holes).
 *  Returns a Float32Array, or null when there is no valid value at all. */
export function fillNodata(values, n) {
  const out = new Float32Array(n * n);
  const ok = new Uint8Array(n * n);
  let any = 0;
  for (let k = 0; k < n * n; k++) {
    const v = values[k];
    if (v !== null && v !== undefined && Number.isFinite(v)) { out[k] = v; ok[k] = 1; any++; }
  }
  if (!any) return null;
  let missing = n * n - any;
  while (missing > 0) {
    const fill = [];
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        const k = r * n + c;
        if (ok[k]) continue;
        let sum = 0, cnt = 0;
        if (c > 0 && ok[k - 1]) { sum += out[k - 1]; cnt++; }
        if (c < n - 1 && ok[k + 1]) { sum += out[k + 1]; cnt++; }
        if (r > 0 && ok[k - n]) { sum += out[k - n]; cnt++; }
        if (r < n - 1 && ok[k + n]) { sum += out[k + n]; cnt++; }
        if (cnt) fill.push([k, sum / cnt]);
      }
    }
    if (!fill.length) break;
    fill.forEach(([k, v]) => { out[k] = v; ok[k] = 1; });
    missing -= fill.length;
  }
  return out;
}

/** Bilinear elevation at lon/lat from a filled grid {values, n, extent}; null outside. */
export function elevationAt(grid, lon, lat) {
  if (!grid?.values) return null;
  const { values, n, extent } = grid;
  const fc = ((lon - extent.west) / (extent.east - extent.west)) * (n - 1);
  const fr = ((lat - extent.south) / (extent.north - extent.south)) * (n - 1);
  if (!(fc >= 0 && fc <= n - 1 && fr >= 0 && fr <= n - 1)) return null;
  const c0 = Math.min(n - 2, Math.floor(fc)), r0 = Math.min(n - 2, Math.floor(fr));
  const tx = fc - c0, ty = fr - r0;
  const v = (r, c) => values[r * n + c];
  return v(r0, c0) * (1 - tx) * (1 - ty) + v(r0, c0 + 1) * tx * (1 - ty)
    + v(r0 + 1, c0) * (1 - tx) * ty + v(r0 + 1, c0 + 1) * tx * ty;
}

/** Scene y of an elevation (before the vertical-exaggeration group scale). */
export const terrainY = (elevation, refElev, ve = 1) => (elevation - refElev) * ve;

// ── Web Mercator tiles for the drape texture ─────────────────────────────────

export function lonLatToTile(lon, lat, z) {
  const n = 2 ** z;
  const latR = (Math.max(-85.0511, Math.min(85.0511, lat)) * Math.PI) / 180;
  return [((lon + 180) / 360) * n, ((1 - Math.asinh(Math.tan(latR)) / Math.PI) / 2) * n];
}

/** {z, x0, y0, nx, ny}: the deepest zoom (<= maxZoom) covering the extent with at most
 *  maxTiles x maxTiles tiles. */
export function tileRange(extent, { maxTiles = 4, maxZoom = 18 } = {}) {
  for (let z = maxZoom; z >= 0; z--) {
    const [x0f, y0f] = lonLatToTile(extent.west, extent.north, z);
    const [x1f, y1f] = lonLatToTile(extent.east, extent.south, z);
    const x0 = Math.floor(x0f), y0 = Math.floor(y0f);
    const nx = Math.floor(Math.min(x1f, 2 ** z - 1e-9)) - x0 + 1;
    const ny = Math.floor(Math.min(y1f, 2 ** z - 1e-9)) - y0 + 1;
    if (nx <= maxTiles && ny <= maxTiles) return { z, x0, y0, nx, ny };
  }
  return { z: 0, x0: 0, y0: 0, nx: 1, ny: 1 };
}

/** Texture uv of a lon/lat on the composed tile canvas (v = 0 at the bottom). */
export function tileUv(lon, lat, range) {
  const [tx, ty] = lonLatToTile(lon, lat, range.z);
  return [(tx - range.x0) / range.nx, 1 - (ty - range.y0) / range.ny];
}

/** Tile URLs of a range ({z}/{x}/{y} filled), row-major from the top-left. */
export function rangeTiles(range) {
  const out = [];
  for (let j = 0; j < range.ny; j++) {
    for (let i = 0; i < range.nx; i++) out.push({ z: range.z, x: range.x0 + i, y: range.y0 + j, col: i, row: j });
  }
  return out;
}

/** Tile limits per texture kind: DEM tiles are served at <= z18, basemaps go to z19 with a 2048 px canvas. */
export const TEXTURE_TILE_LIMITS = { dem: { maxTiles: 4, maxZoom: 18 }, basemap: { maxTiles: 8, maxZoom: 19 } };

/** tileRange for a texture kind ('dem' | 'basemap'). */
export const tileRangeFor = (extent, kind = 'dem') => tileRange(extent, TEXTURE_TILE_LIMITS[kind] || TEXTURE_TILE_LIMITS.dem);

/** Fill a {z}/{x}/{y} template for a tile from rangeTiles (any placeholder order). */
export const tileUrl = (template, t) => template.replace('{z}', t.z).replace('{x}', t.x).replace('{y}', t.y);

// ── Mesh ─────────────────────────────────────────────────────────────────────

/**
 * Terrain mesh buffers from an n x n elevation grid over `extent`.
 * Returns { positions, normals, uvs, indices, filled, refElev, vertexCount, triangleCount }
 * or null when the grid has no valid elevation. `refElev` defaults to the elevation at
 * the origin (else the grid minimum). `tiles` (a tileRange) gives Web Mercator uvs for
 * the drape texture; without it uvs span the extent linearly.
 */
export function buildTerrainMesh({ elevations, n, extent, origin, refElev = null, ve = 1, tiles = null }) {
  const filled = fillNodata(elevations, n);
  if (!filled) return null;
  let ref = refElev;
  if (ref === null || ref === undefined || !Number.isFinite(ref)) {
    ref = elevationAt({ values: filled, n, extent }, origin.lon0, origin.lat0);
    if (ref === null) ref = filled.reduce((m, v) => (v < m ? v : m), Infinity);
  }
  const positions = new Float32Array(n * n * 3);
  const uvs = new Float32Array(n * n * 2);
  for (let r = 0; r < n; r++) {
    const lat = extent.south + ((extent.north - extent.south) * r) / (n - 1);
    for (let c = 0; c < n; c++) {
      const k = r * n + c;
      const lon = extent.west + ((extent.east - extent.west) * c) / (n - 1);
      const [x, z] = toLocal(lon, lat, origin);
      positions[k * 3] = x;
      positions[k * 3 + 1] = terrainY(filled[k], ref, ve);
      positions[k * 3 + 2] = z;
      const [u, v] = tiles ? tileUv(lon, lat, tiles) : [c / (n - 1), r / (n - 1)];
      uvs[k * 2] = u;
      uvs[k * 2 + 1] = v;
    }
  }
  const indices = new Uint32Array((n - 1) * (n - 1) * 6);
  let t = 0;
  for (let r = 0; r < n - 1; r++) {
    for (let c = 0; c < n - 1; c++) {
      const a = r * n + c, b = a + 1, d = a + n, e = d + 1;
      // counter-clockwise seen from above (+y): a(sw) -> d(nw) -> b(se), b -> d -> e(ne)
      indices[t++] = a; indices[t++] = d; indices[t++] = b;
      indices[t++] = b; indices[t++] = d; indices[t++] = e;
    }
  }
  const normals = vertexNormals(positions, indices);
  return { positions, normals, uvs, indices, filled, refElev: ref, vertexCount: n * n, triangleCount: indices.length / 3 };
}

/** Area-weighted vertex normals. */
export function vertexNormals(positions, indices) {
  const normals = new Float32Array(positions.length);
  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i] * 3, b = indices[i + 1] * 3, c = indices[i + 2] * 3;
    const ux = positions[b] - positions[a], uy = positions[b + 1] - positions[a + 1], uz = positions[b + 2] - positions[a + 2];
    const vx = positions[c] - positions[a], vy = positions[c + 1] - positions[a + 1], vz = positions[c + 2] - positions[a + 2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    for (const p of [a, b, c]) { normals[p] += nx; normals[p + 1] += ny; normals[p + 2] += nz; }
  }
  for (let p = 0; p < normals.length; p += 3) {
    const len = Math.hypot(normals[p], normals[p + 1], normals[p + 2]) || 1;
    normals[p] /= len; normals[p + 1] /= len; normals[p + 2] /= len;
  }
  return normals;
}

/** Buffers of a mesh result, for the worker's transfer list. */
export const terrainMeshBuffers = (m) => (m ? [m.positions.buffer, m.normals.buffer, m.uvs.buffer, m.indices.buffer, m.filled.buffer] : []);

// ── Draped models ────────────────────────────────────────────────────────────

/** Ground elevation of a cell: its own `surface` (model topography) or the DEM there. */
export function groundOf(cell, grid) {
  if (typeof cell.surface === 'number') return cell.surface;
  if (cell.lon === undefined) return null;
  return elevationAt(grid, cell.lon, cell.lat);
}

/**
 * Ribbon geometry for a 2D fence draped under the terrain: one column per station x
 * along `entry.line` (WGS84 ends of xRange), top at the ground (model surface or DEM),
 * bottom `maxDepth` below it. Returns { positions, uvs, indices } in the local frame
 * (y before VE) or null when the line is off the DEM.
 */
export function drapedFenceGeometry(entry, grid, origin, refElev) {
  const [start, end] = entry.line || [];
  const xr = entry.model?.xRange;
  if (!start || !end || !xr) return null;
  const cells = entry.cells || [];
  const xs = [...new Set(cells.map((c) => c.x))].sort((a, b) => a - b);
  if (xs.length < 2) return null;
  const maxDepth = entry.model?.depthRange?.[1] || 1;
  const surfaceByX = new Map();
  cells.forEach((c) => { if (typeof c.surface === 'number' && !surfaceByX.has(c.x)) surfaceByX.set(c.x, c.surface); });
  const span = (xr[1] - xr[0]) || 1;
  const positions = new Float32Array(xs.length * 2 * 3);
  const uvs = new Float32Array(xs.length * 2 * 2);
  let missing = 0;
  xs.forEach((x, i) => {
    const t = (x - xr[0]) / span;
    const lon = start[0] + (end[0] - start[0]) * t;
    const lat = start[1] + (end[1] - start[1]) * t;
    let g = surfaceByX.has(x) ? surfaceByX.get(x) : elevationAt(grid, lon, lat);
    if (g === null) { missing++; g = refElev; }
    const [px, pz] = toLocal(lon, lat, origin);
    const top = g - refElev;
    positions.set([px, top, pz, px, top - maxDepth, pz], i * 6);
    const u = (x - xs[0]) / ((xs[xs.length - 1] - xs[0]) || 1);
    uvs.set([u, 1, u, 0], i * 4);
  });
  if (missing === xs.length) return null;
  const indices = new Uint32Array((xs.length - 1) * 6);
  for (let i = 0; i < xs.length - 1; i++) {
    const a = i * 2, b = a + 1, c = a + 2, d = a + 3;
    indices.set([a, b, c, c, b, d], i * 6);
  }
  return { positions, uvs, indices };
}

/** Point positions of a 3D model's cells hung from the ground: y = ground - depth - refElev. */
export function drapedVoxelPositions(cells, grid, origin, refElev) {
  const pos = new Float32Array(cells.length * 3);
  cells.forEach((c, i) => {
    const g = groundOf(c, grid);
    const [x, z] = toLocal(c.lon, c.lat, origin);
    pos[i * 3] = x;
    pos[i * 3 + 1] = (g === null ? refElev : g) - c.depth - refElev;
    pos[i * 3 + 2] = z;
  });
  return pos;
}
